import bark from "bark-js";
import { ProductTypeRevision } from "./api/product-api";

export interface SearchInStringExec {
    exec(text: string): string[] | null;
}

export function parseToDateAndFormatIfExists(time?: string | null) {
    if (!time) {
        return "";
    }

    return new Date(Date.parse(time)).toLocaleString();
}

export function trimText(text: string, maxLength: number) {
    if (text.length > maxLength) {
        let trimmed = text.substr(0, maxLength - 3);
        trimmed += "...";
        return trimmed;
    }

    return text;
}

export function group<T, TKey = string>(arr: T[], groupKey: (v: T) => string, keyMap: (v: T) => TKey = groupKey as unknown as (v: T) => TKey) {
    const groups = [] as {
        key: TKey,
        items: T[]
    }[];
    const groupsMap: { [key: string]: T[] } = {};

    for (let i = 0; i < arr.length; i++) {
        const item = arr[i];
        const key = groupKey(item);

        if (!groupsMap[key]) {
            groupsMap[key] = [];
            groups.push({ key: keyMap(item), items: groupsMap[key] });
        }

        groupsMap[key].push(item);
    }

    return groups;
}

export function getExampleSerialNumber(type: string) {
    if (type === "serial-8-base32") {
        return "ABCD9876";
    }

    if (type === "serial-6-base16") {
        return "000000";
    }

    return "<SERIAL>";
}

export function generateExampleFieldsForLabel(metadata: { [key: string]: string }) {
    const output = {} as { [key: string]: any };

    for (const prop in metadata) {
        if (!prop.startsWith("field-")) {
            continue;
        }

        if (prop === "field-gtin13") {
            output[prop] = "1234567891234";
        }

        if (prop === "field-gtin14" || prop === "field-gtin") {
            output[prop] = "01234567891234";
        }

        if (prop === "field-model") {
            output[prop] = "ABC-DE-1.0";
        }

        if (metadata[prop] === "date") {
            output[prop] = new Date();
        }

        if (metadata[prop] === "serial-6-base16" || metadata[prop] === "serial-8-base32") {
            output[prop] = getExampleSerialNumber(metadata[prop]);
        }

        if (metadata[prop] === "gtin-lot") {
            output[prop] = "12345678901234-ABCD1234"
        }

        if (metadata[prop] === "gtin-lot-8-date") {
            const date = new Date();

            output[prop] = "12345678901234-"
                + date.getFullYear()
                + (date.getMonth() + 1).toString().padStart(2, "0")
                + date.getDate().toString().padStart(2, "0");
        }
    }

    return output;
}


export function generateLabelFields(
    provisioned: {
        serialNumber?: string | null,
        date?: Date,
        revision?: ProductTypeRevision | null,
        default?: (prop: string) => void
    },
    fields: { [key: string]: any } = {},
    shouldGenerate: (prop: string) => boolean = () => true
) {
    if (shouldGenerate("field-date")) {
        fields["field-date"] = provisioned.date ?? new Date();
    }

    if (shouldGenerate("field-serial") && (provisioned.serialNumber || provisioned.serialNumber === "")) {
        fields["field-serial"] = provisioned.serialNumber;
    }

    if (shouldGenerate("field-model") && provisioned.revision) {
        fields["field-model"] = `${provisioned.revision.productType?.model || provisioned.revision.productType?.name}-${provisioned.revision.modelName}`;
    }

    if (shouldGenerate("field-version") && provisioned.revision) {
        fields["field-version"] = provisioned.revision.modelName;
    }

    if (shouldGenerate("field-gtin") && provisioned.revision && (provisioned.revision.gtin || provisioned.revision.productType?.gtin)) {
        const gtin = provisioned.revision.gtin || provisioned.revision.productType!.gtin!;
        fields["field-gtin"] = gtin;

        if (gtin.length === 14) {
            fields["field-gtin13"] = gtin.slice(1);
        }
    }

    if (shouldGenerate("field-gtin13") && provisioned.revision && (provisioned.revision.gtin || provisioned.revision.productType?.gtin)) {
        const gtin = provisioned.revision.gtin || provisioned.revision.productType!.gtin!;

        fields["field-gtin13"] = gtin.length === 14 ? gtin.slice(1) : gtin;
    }

    return fields;
}

export function fillLabelFields(
    metadata: { [key: string]: string },
    fill: {
        serialNumber?: string | null,
        revision?: ProductTypeRevision | null,
        default?: (prop: string) => void
    },
    fields: { [key: string]: any },
) {
    generateLabelFields(fill, fields, (prop) => prop in metadata);

    if (fill.default) {
        for (const prop in metadata) {
            if (prop in fields) {
                continue;
            }

            fill.default?.(prop);
        }
    }

    return fields;
}

export async function fillLabelFieldsAsync(
    metadata: { [key: string]: string },
    fill: {
        serialNumber?: string | null | (() => string | Promise<string>),
        revision?: ProductTypeRevision | null,
        default?: (prop: string) => void
    },
    fields: { [key: string]: any },
) {
    const syncFill: Parameters<typeof fillLabelFields>[1] = {
        revision: fill.revision,
        default: fill.default
    };

    for (const prop in metadata) {
        if (prop === "field-serial" && fill.serialNumber) {
            syncFill.serialNumber = typeof fill.serialNumber === "function" ? await fill.serialNumber() : fill.serialNumber;
        }
    }

    return fillLabelFields(metadata, syncFill, fields);
}

export function findSerialNumberByRegex(text: string, regexes: SearchInStringExec[]) {
    let match;

    for (let i = 0; i < regexes.length; i++) {
        const regex = regexes[i];

        match = regex.exec(text);

        if (!match || match.length < 2) {
            continue;
        }

        break;
    }

    if (!match || match.length < 2) {
        return null;
    }

    return match.slice(1).join("-");
}

export function createGS1SearchExec(ais: { ai: string, include?: boolean, regex?: RegExp }[]) {
    return {
        exec(text: string) {
            let data;

            try {
                data = bark(text);
            }
            catch (ex) {
                return null;
            }

            const result = [text];

            for (let i = 0; i < ais.length; i++) {
                const searchAi = ais[i];
                const element = data.elements.find(v => v.ai === searchAi.ai);

                if (!element) {
                    return null;
                }

                if (searchAi.regex) {
                    const v = searchAi.regex.exec(element.value);

                    if (!v) {
                        return null;
                    }

                    if (searchAi.include !== false) {
                        result.push(...v.slice(1));
                    }
                }
                else {
                    if (searchAi.include !== false) {
                        result.push(element.value);
                    }
                }
            }

            return result;
        }
    };
}
